import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from '@ts-core/lib/apollo';

import { DataFetcherHookResult } from '@ts-core/core/types';
import { formatValue } from '@ts-core/utils/dataTableUtils';

import { AssignmentAnswer } from '../types';
import {
  getAssignmentAnswersQuery,
  GetAssignmentAnswersQueryResult,
  GetAssignmentAnswersQueryVariables,
} from '../graphql/queries/getAssignmentAnswersQuery';

type AssignmentAnswersHookResult = DataFetcherHookResult<AssignmentAnswer[], 'answers'> & {
  onGetXlsxData: (answers: { _original: AssignmentAnswer }[]) => void;
};

interface AssignmentAnswersHookInput {
  courseId: string;
  assignmentId: string;
}

export default function useAssignmentAnswers(input: AssignmentAnswersHookInput): AssignmentAnswersHookResult {
  const { courseId, assignmentId } = input;
  const intl = useIntl();

  const { data, loading, errorMessage } = useQuery<GetAssignmentAnswersQueryResult, GetAssignmentAnswersQueryVariables>(getAssignmentAnswersQuery, {
    variables: { courseId, assignmentId },
  });
  const answers = data?.answers;

  /**
   * Get xlsx data
   */
  const onGetXlsxData = useCallback(
    (sortedData: { _original: AssignmentAnswer }[]) => {
      if (!answers) return [];

      const headers = [intl.formatMessage({ id: 'studentsTable.column.name' }), intl.formatMessage({ id: 'studentsTable.column.email' })];

      const hasGroups = answers.some((s) => s.student.group);
      if (hasGroups) {
        headers.push(intl.formatMessage({ id: 'assignmentResultsTable.group' }));
      }
      headers.push(intl.formatMessage({ id: 'result' }));

      const xlsData = [headers];
      sortedData.forEach((data) => {
        const { _original: answer } = data;

        const answerData = [answer.student.name, answer.student.email];
        if (hasGroups) {
          answerData.push(answer.student.group?.name || '-');
        }
        answerData.push(formatValue(answer.score, answer.maxScore));
        xlsData.push(answerData);
      });
      return xlsData;
    },
    [intl, answers]
  );

  return { loading, error: errorMessage, answers, onGetXlsxData };
}
